import {
  useEffect, useId, useRef, type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { clsx } from "clsx";
import { X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useIsMobile } from "@/shared/hooks/useIsMobile";

export type ResponsiveDialogSize = "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";

/** "modal" = centered on desktop (default). "drawer" = slides in from the right on desktop.
 *  Both stay a bottom sheet on mobile. */
export type ResponsiveDialogVariant = "modal" | "drawer";

type Props = {
  open: boolean
  onClose: () => void
  title: string
  /** Rendered inside the heading, before the title (e.g. a logo): `alt=""` keeps the accessible name
   *  to the title alone. */
  titleAdornment?: ReactNode
  description?: string
  children: ReactNode
  footer?: ReactNode
  /** Full-bleed banner rendered in place of the default white title header (e.g. a coloured hero).
   *  When set, `title` is kept as the visually-hidden accessible name and the close button overlays
   *  the banner. Callers that omit it get the default header unchanged. */
  hero?: ReactNode
  /** Disables backdrop / escape close (e.g. while submitting) */
  dismissible?: boolean
  /** Desktop max-width for the "modal" variant. Mobile stays a bottom sheet regardless. Defaults to "md". */
  size?: ResponsiveDialogSize
  variant?: ResponsiveDialogVariant
};

const SIZE_MAX_WIDTH: Record<ResponsiveDialogSize, string> = {
  "md": "max-w-md",
  "lg": "max-w-lg",
  "xl": "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
};

const useLockBodyScroll = (active: boolean) => {
  useEffect(() => {
    if (!active) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [active]);
};

const useEscapeClose = (active: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!active) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [active, onClose]);
};

const FOCUSABLE_SELECTOR = [
  "a[href]", "button:not([disabled])", "textarea:not([disabled])",
  "input:not([disabled])", "select:not([disabled])", "[tabindex]:not([tabindex='-1'])",
].join(",");

// Captures focus on open, traps Tab/Shift-Tab within the panel (only intervening at the
// boundaries, so native form-control tabbing inside the panel is untouched), and restores focus
// to the previously focused element on close.
const useFocusTrap = (active: boolean, panelRef: React.RefObject<HTMLDivElement | null>) => {
  useEffect(() => {
    if (!active) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    panel?.focus();

    const handler = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeEl = document.activeElement;
      if (event.shiftKey && (activeEl === first || activeEl === panelRef.current)) {
        event.preventDefault();
        last.focus();
      }
      else if (!event.shiftKey && (activeEl === last || activeEl === panelRef.current)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
      // Only restore focus if the trigger is still in the document; if it has unmounted (e.g. the
      // list behind the dialog re-rendered), focusing it would silently drop focus to <body>.
      if (previouslyFocused && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus?.();
      }
    };
  }, [active, panelRef]);
};

const FADE_TRANSITION = { duration: 0.15, ease: "easeOut" } as const;
const SLIDE_TRANSITION = { duration: 0.2, ease: "easeOut" } as const;

const wrapperClass = (isMobile: boolean, isDrawer: boolean) => clsx(
  "fixed inset-0 z-50 flex",
  isMobile ? "items-stretch justify-center" : isDrawer ? "justify-end" : "items-center justify-center p-6",
);

const panelClass = (isMobile: boolean, isDrawer: boolean, size: ResponsiveDialogSize, hasHero: boolean) => clsx(
  "relative z-10 flex flex-col bg-white shadow-2xl outline-none dark:bg-gray-900",
  hasHero && "overflow-hidden",
  isMobile
    ? "mt-auto max-h-[92dvh] w-full rounded-t-3xl pb-[env(safe-area-inset-bottom)]"
    : isDrawer
      ? "h-full w-[480px] max-w-[94vw] rounded-l-3xl"
      : `max-h-[90vh] w-full ${SIZE_MAX_WIDTH[size]} rounded-3xl`,
);

// Honour prefers-reduced-motion: skip the slide/scale and just cross-fade.
const panelMotion = (isMobile: boolean, isDrawer: boolean, reduceMotion: boolean) => {
  const initial = reduceMotion ? { opacity: 0 } : isMobile ? { y: "100%" } : isDrawer ? { x: "100%" } : { opacity: 0, scale: 0.96 };
  const animate = reduceMotion ? { opacity: 1 } : isMobile ? { y: 0 } : isDrawer ? { x: 0 } : { opacity: 1, scale: 1 };
  return {
    initial, animate, exit: initial, transition: isMobile || isDrawer ? SLIDE_TRANSITION : FADE_TRANSITION,
  };
};

const CloseButton = ({ onClose, className }: { onClose: () => void, className: string }) => (
  <button type="button" onClick={onClose} aria-label="Fermer" className={className}>
    <X size={20} aria-hidden />
  </button>
);

type DialogHeaderProps = {
  hero?: ReactNode
  title: string
  titleAdornment?: ReactNode
  titleId: string
  description?: string
  descriptionId: string
  dismissible: boolean
  onClose: () => void
};

// The hero variant keeps the title/description as the visually-hidden accessible name + description
// (the caller's banner is the visible header); the default variant renders a plain white header.
const DialogHeader = ({
  hero, title, titleAdornment, titleId, description, descriptionId, dismissible, onClose,
}: DialogHeaderProps) => {
  if (hero) {
    return (
      <div className="relative shrink-0">
        <h2 id={titleId} className="sr-only">{title}</h2>
        {description && <p id={descriptionId} className="sr-only">{description}</p>}
        {hero}
        {dismissible && (
          <CloseButton
            onClose={onClose}
            className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30"
          />
        )}
      </div>
    );
  }
  return (
    <header className="flex items-start justify-between gap-4 px-6 pt-6 pb-2">
      <div>
        <h2
          id={titleId}
          className={clsx("text-lg font-bold text-gray-900 dark:text-white", titleAdornment && "inline-flex items-center justify-start gap-2")}
        >
          {titleAdornment}
          {title}
        </h2>
        {description && (
          <p id={descriptionId} className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
        )}
      </div>
      {dismissible && (
        <CloseButton
          onClose={onClose}
          className="-mr-2 -mt-2 inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        />
      )}
    </header>
  );
};

export const ResponsiveDialog = ({
  open,
  onClose,
  title,
  titleAdornment,
  description,
  children,
  footer,
  dismissible = true,
  size = "md",
  variant = "modal",
  hero,
}: Props) => {
  const isMobile = useIsMobile();
  const titleId = useId();
  const descriptionId = useId();
  const reduceMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  useLockBodyScroll(open);
  useEscapeClose(open && dismissible, onClose);
  useFocusTrap(open, panelRef);

  const handleBackdropClick = () => {
    if (dismissible) onClose();
  };

  if (typeof document === "undefined") return null;

  const isDrawer = variant === "drawer" && !isMobile;
  const motionProps = panelMotion(isMobile, isDrawer, Boolean(reduceMotion));

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className={wrapperClass(isMobile, isDrawer)}>
          <motion.div
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={FADE_TRANSITION}
            onClick={handleBackdropClick}
            aria-hidden
          />
          <motion.div
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={description ? descriptionId : undefined}
            initial={motionProps.initial}
            animate={motionProps.animate}
            exit={motionProps.exit}
            transition={motionProps.transition}
            className={panelClass(isMobile, isDrawer, size, Boolean(hero))}
          >
            <DialogHeader
              hero={hero}
              title={title}
              titleAdornment={titleAdornment}
              titleId={titleId}
              description={description}
              descriptionId={descriptionId}
              dismissible={dismissible}
              onClose={onClose}
            />
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {children}
            </div>
            {footer && (
              <footer className="border-t border-gray-100 px-6 py-4 dark:border-gray-800">
                {footer}
              </footer>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

ResponsiveDialog.displayName = "ResponsiveDialog";
